import { initializeApp } from 'firebase/app';
import { Capacitor } from '@capacitor/core';
import {
  FirebaseMessaging,
  Notification as NativeNotification,
} from '@capacitor-firebase/messaging';
import { API_ENDPOINTS, API_URL } from '../config/constants.ts';
import { SW_VERSION } from '../config/swVersion.ts';

export const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);
export const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
export const isInStandaloneMode = () =>
  window.matchMedia('(display-mode: standalone)').matches ||
  (window.navigator as any).standalone === true;

const isNative = Capacitor.isNativePlatform();
const supportsWebPush =
  typeof window !== 'undefined' &&
  'Notification' in window &&
  'serviceWorker' in navigator;

// Native apps use Capacitor/APNs/FCM. Browsers use Firebase web messaging.
export const isPushNotificationSupported =
  isNative || (supportsWebPush && (!isIOS || isInStandaloneMode()));

const firebaseConfig = {
  apiKey: 'AIzaSyDVNF-gsXJmRMb-q-RU_qGfITc-vh3Ik50',
  authDomain: 'ultra-5827b.firebaseapp.com',
  projectId: 'ultra-5827b',
  storageBucket: 'ultra-5827b.appspot.com',
  messagingSenderId: '157349571397',
  appId: '1:157349571397:web:ed007e3ab0dc5309132c2c',
  measurementId: 'G-44RQ7BSE75',
};

let lastSentToken = '';
let nativeRegistration: Promise<string | null> | null = null;
let nativeTokenListenerInitialized = false;

const saveToken = async (token: string): Promise<void> => {
  localStorage.setItem('fcm_token', token);

  if (token === lastSentToken) return;

  const authToken = localStorage.getItem('auth_token');
  if (!authToken) return;

  const response = await fetch(`${API_URL}${API_ENDPOINTS.user.updateUserToken}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${authToken}`,
    },
    body: JSON.stringify({ token }),
  });

  if (!response.ok) {
    throw new Error(`Failed to save notification token (${response.status})`);
  }

  lastSentToken = token;
};

const requestNativeNotificationPermission = async (): Promise<string | null> => {
  if (nativeRegistration) return nativeRegistration;

  nativeRegistration = (async () => {
    const support = await FirebaseMessaging.isSupported();
    if (!support.isSupported) return null;

    let permission = await FirebaseMessaging.checkPermissions();
    if (permission.receive === 'prompt') {
      permission = await FirebaseMessaging.requestPermissions();
    }
    if (permission.receive !== 'granted') return null;

    if (!nativeTokenListenerInitialized) {
      nativeTokenListenerInitialized = true;
      await FirebaseMessaging.addListener('tokenReceived', async ({ token }) => {
        try {
          await saveToken(token);
        } catch (error) {
          console.error('Error saving refreshed FCM token:', error);
        }
      });
    }

    const { token } = await FirebaseMessaging.getToken();
    if (!token) return null;

    await saveToken(token);
    return token;
  })().finally(() => {
    // Keep concurrent requests deduplicated, but allow a later retry after
    // permission changes, a temporary network error, or a failed API save.
    nativeRegistration = null;
  });

  return nativeRegistration;
};

const requestWebNotificationPermission = async (): Promise<string | null> => {
  if (!supportsWebPush) return null;

  let permission = Notification.permission;
  if (permission !== 'granted') {
    permission = await Notification.requestPermission();
  }
  if (permission !== 'granted') return null;

  const [{ getMessaging, getToken }] = await Promise.all([
    import('firebase/messaging'),
  ]);
  const messaging = getMessaging(initializeApp(firebaseConfig));
  const registration = await navigator.serviceWorker.register(
    `/firebase-messaging-sw.js?v=${SW_VERSION}`,
    { updateViaCache: 'none' },
  );
  await registration.update();
  await navigator.serviceWorker.ready;

  const token = await getToken(messaging, {
    serviceWorkerRegistration: registration,
  });
  if (!token) return null;

  await saveToken(token);
  return token;
};

export const requestNotificationPermission = async (): Promise<string | null> => {
  try {
    return isNative
      ? await requestNativeNotificationPermission()
      : await requestWebNotificationPermission();
  } catch (error) {
    console.error('Error requesting notification permission:', error);
    return null;
  }
};

export const onMessageListener = () => {
  if (isNative) {
    return new Promise<{ notification: NativeNotification }>((resolve) => {
      FirebaseMessaging.addListener(
        'notificationReceived',
        ({ notification }) => resolve({ notification }),
      );
    });
  }

  if (!supportsWebPush) return new Promise(() => undefined);

  return new Promise(async (resolve) => {
    const { getMessaging, onMessage } = await import('firebase/messaging');
    const messaging = getMessaging(initializeApp(firebaseConfig));
    onMessage(messaging, resolve);
  });
};

export const initializeFirebaseMessaging = async (): Promise<void> => {
  const token = await requestNotificationPermission();
  console.log(
    token
      ? `Push notifications initialized on ${Capacitor.getPlatform()}`
      : `Push notifications unavailable on ${Capacitor.getPlatform()}`,
  );
};

export default {
  requestNotificationPermission,
  onMessageListener,
  initializeFirebaseMessaging,
  isPushNotificationSupported,
};
